import React from 'react';
import { Smartphone, Mail, Phone, ExternalLink } from 'lucide-react';

const translations = {
  pt: {
    contactTitle: 'Entre em contato:',
    contactSubtitle: 'Entre em contato com nossa equipe de suporte ao cliente.',
    info: 'Info:',
    quickLinks: 'Links Rápidos',
    home: 'Início',
    games: 'Jogos',
    features: 'Recursos',
    rights: 'Todos os direitos reservados.',
  },
  en: {
    contactTitle: 'Contact us:',
    contactSubtitle: 'Please contact our customer support team.',
    info: 'Info:',
    quickLinks: 'Quick Links',
    home: 'Home',
    games: 'Games',
    features: 'Features',
    rights: 'All rights reserved.',
  }
};

const footerText = {
  pt: 'Serviço de SUBSCRIÇÃO – LAGUNAPLAY. Este serviço custa €4,20/semana (2,1€/ SMS) IVA incluído à taxa legal, renovado automaticamente. Ao inserir o pin nesta página está a confirmar que deseja subscrever este serviço e passará a receber semanalmente downloads que permitirá aceder a jogos ilimitados. Para cancelar o serviço, envie SMS com o texto SAIR para 62961. Serviço de Apoio ao cliente 707450201, ou envie email para help@clickzillas.com, Clickzillas B.V. Ao inscrever-se e/ou utilizar o serviço, reconhece e confirma que leu os termos e condições e a politica de privacidade. carrega aqui para leres os Termos e Condições do serviço.',
  en: 'SUBSCRIPTION SERVICE – LAGUNAPLAY. This service costs €4.20/week (€2.1/ SMS) VAT included at the legal rate, automatically renewed. By entering the pin on this page you are confirming that you wish to subscribe to this service and will receive weekly downloads that will allow you to access unlimited games. To cancel the service, send SMS with the text EXIT to 62961. Customer Support Service 707450201, or send email to help@clickzillas.com, Clickzillas B.V. By registering and/or using the service, you acknowledge and confirm that you have read the terms and conditions and privacy policy. click here to read the Service Terms and Conditions.',
};

function Footer({ language }) {
  const t = translations[language];
  const footerLegal = footerText[language];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="footer" className="relative bg-gradient-to-b from-darker via-surface/30 to-darker border-t border-primary/20">
      {/* Gradient top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary via-secondary to-transparent"></div>
      
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-purple opacity-5"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-8">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-primary via-secondary to-accent rounded-2xl flex items-center justify-center shadow-purple-glow">
                  <Smartphone className="w-9 h-9 text-white" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-primary to-accent rounded-2xl blur-xl opacity-50"></div>
              </div>
              <div>
                <span className="font-heading text-2xl font-bold text-white tracking-wider block">
                  Mundo<span className="gradient-text">Digital</span>24
                </span>
                <span className="text-sm text-accent font-body tracking-widest">Premium Mobile Games</span>
              </div>
            </div>
            
            {/* Legal Notice - Prominent with purple styling */}
            <div className="bg-gradient-to-br from-surface/80 to-surface/40 backdrop-blur-xl border border-primary/30 rounded-2xl p-6 mb-6 shadow-lg">
              <p className="text-slate-300 font-body text-sm leading-relaxed">
                {footerLegal}
              </p>
            </div>

            <p className="text-slate-400 font-body text-sm">
              {t.contactSubtitle} <span className="text-accent font-semibold">help@clickzillas.com</span> | <span className="text-accent font-semibold">707450201</span>
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-bold text-white text-xl mb-6">{t.quickLinks}</h3>
            <ul className="space-y-4">
              {[
                { label: t.home, id: 'hero' },
                { label: t.features, id: 'features' },
                { label: t.games, id: 'games' },
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-slate-400 hover:text-white font-body transition-all duration-200 flex items-center gap-3 group"
                  >
                    <span className="w-2 h-2 bg-primary rounded-full group-hover:bg-accent transition-colors group-hover:scale-150"></span>
                    <span className="group-hover:translate-x-1 transition-transform">{link.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Section */}
          <div>
            <h3 className="font-heading font-bold text-white text-xl mb-6">{t.contactTitle}</h3>
            <p className="text-slate-400 font-body text-sm mb-6">
              {t.contactSubtitle}
            </p>
            
            <div className="space-y-4">
              <a 
                href="mailto:help@clickzillas.com" 
                className="flex items-center gap-4 text-slate-400 hover:text-accent transition-all duration-300 group p-3 rounded-xl hover:bg-primary/10"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 border border-primary/30 rounded-xl flex items-center justify-center group-hover:border-primary group-hover:shadow-purple-glow transition-all">
                  <Mail className="w-6 h-6" />
                </div>
                <span className="font-body">help@clickzillas.com</span>
              </a>
              
              <a 
                href="tel:707450201" 
                className="flex items-center gap-4 text-slate-400 hover:text-accent transition-all duration-300 group p-3 rounded-xl hover:bg-primary/10"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 border border-primary/30 rounded-xl flex items-center justify-center group-hover:border-primary group-hover:shadow-purple-glow transition-all">
                  <Phone className="w-6 h-6" />
                </div>
                <span className="font-body">707450201</span>
              </a>

              <a 
                href="https://mobileinfo.biz" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-slate-400 hover:text-accent transition-all duration-300 group p-3 rounded-xl hover:bg-primary/10"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 border border-primary/30 rounded-xl flex items-center justify-center group-hover:border-primary group-hover:shadow-purple-glow transition-all">
                  <ExternalLink className="w-6 h-6" />
                </div>
                <span className="font-body">{t.info} mobileinfo.biz</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-primary/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 font-body text-sm">
              © {new Date().getFullYear()} mundodigital24.com. {t.rights}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
